import Map from 'eemovel-map-library/src/data/google-maps/map';

/**
 * Reposição do `google.maps.drawing.DrawingManager`, removido da Maps
 * JavaScript API na versão 3.65.
 *
 * O construtor continua no namespace, mas lança ao ser instanciado. A
 * `eemovel-map-library` o cria no meio do `Map.initMap`, então a exceção
 * interrompia todo o resto da função — inclusive o
 * `new google.maps.places.Autocomplete(...)` do campo de endereço, que é a
 * última coisa que o `initMap` faz. Daí o sintoma confuso: o mapa aparecia
 * normalmente, mas a busca por endereço e o desenho não respondiam.
 *
 * Este shim substitui o construtor antes do `initMap` rodar e reimplementa o
 * desenho de polígono sobre `google.maps.Polygon`, expondo só a superfície que a
 * lib consome: `getDrawingMode`, `setDrawingMode` e o evento `polygoncomplete`.
 * O enum `OverlayType` o Google manteve, então não precisa ser recriado.
 *
 * Cobre polígono porque é o único modo do DrawingManager que este app usa
 * (`Map.drawPolygon`). A mão livre da lib nunca dependeu do DrawingManager: ela
 * desenha com listeners próprios e só chamava `setDrawingMode(null)`, que aqui é
 * inofensivo. `Map.drawCircle` e `Map.drawPolyLine` não são chamados em nenhum
 * ponto do app — se algum dia forem, seguem o mesmo padrão deste arquivo.
 *
 * Mora no app, e não na lib, porque a `eemovel-map-library` é compartilhada com
 * outros produtos. Quando a lib deixar de usar o DrawingManager, apague este
 * arquivo e a chamada em `src/index.tsx`.
 *
 * Fixar a versão do loader não é alternativa: `v=quarterly` já entrega a
 * 3.65.14a, que também não tem o DrawingManager.
 */

type DrawingMode = google.maps.drawing.OverlayType | null;

/**
 * Só o que a lib passa no construtor. Tipo local porque o
 * `google.maps.drawing.DrawingManagerOptions` saiu do `@types/google.maps`
 * junto com a remoção da API.
 */
type DrawingManagerOptions = {
  map?: google.maps.Map | null;
  drawingControl?: boolean;
  polygonOptions?: google.maps.PolygonOptions;
  polylineOptions?: google.maps.PolylineOptions;
};

const CROSSHAIR = 'crosshair';

const MIN_POLYGON_VERTICES = 3;

/** Folga para o `dblclick` que encerra o desenho passar sem virar zoom. */
const DOUBLE_CLICK_ZOOM_RESTORE_MS = 500;

class DrawingManagerShim {
  private options: DrawingManagerOptions;

  private mode: DrawingMode = null;

  private listeners: google.maps.MapsEventListener[] = [];

  /** Polígono em construção. Vira `null` quando o desenho é concluído. */
  private preview: google.maps.Polygon | null = null;

  private path: google.maps.MVCArray<google.maps.LatLng> | null = null;

  /** Verdadeiro entre o `mouseup` que encerra o desenho e o `dblclick` seguinte. */
  private pendingDoubleClick = false;

  constructor(options: DrawingManagerOptions = {}) {
    this.options = options;
  }

  getMap(): google.maps.Map | null | undefined {
    return this.options.map;
  }

  setMap(map: google.maps.Map | null): void {
    this.options = { ...this.options, map };
  }

  setOptions(options: DrawingManagerOptions): void {
    this.options = { ...this.options, ...options };
  }

  getDrawingMode(): DrawingMode {
    return this.mode;
  }

  setDrawingMode(mode: DrawingMode): void {
    // Sair cedo quando nada muda importa: o `mouseup` do desenho à mão livre da
    // lib chama `setDrawingMode(null)` com o modo já nulo, e restaurar o cursor
    // ali atropelaria a configuração que a mão livre acabou de aplicar.
    if (mode === this.mode) {
      return;
    }

    this.discardPreview();
    this.mode = mode;

    if (mode === google.maps.drawing.OverlayType.POLYGON) {
      this.armPolygon();
      return;
    }

    // `null`, e também os modos que este app não usa.
    this.restoreMapInteraction();
  }

  /** Clique adiciona vértice, duplo clique encerra. */
  private armPolygon(): void {
    const map = this.options.map;
    if (!map) {
      return;
    }

    // `disableDoubleClickZoom` evita que o duplo clique que encerra o desenho
    // também aplique o zoom padrão do Maps.
    map.setOptions({
      draggableCursor: CROSSHAIR,
      disableDoubleClickZoom: true,
    });

    // O polígono é criado com um caminho vazio e os vértices vão para o MVCArray
    // que ele mesmo expõe. Passar um MVCArray próprio em `paths` faria o Google
    // interpretá-lo como uma lista de caminhos, e o `getPath().getArray()` que a
    // lib usa quebraria.
    const polygon = new google.maps.Polygon({
      ...this.options.polygonOptions,
      map,
      paths: [[]],
    });

    this.preview = polygon;
    this.path = polygon.getPath();

    this.listeners.push(
      map.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (this.path && event.latLng) {
          this.path.push(event.latLng);
        }
      })
    );

    this.listenForRelease(map);
  }

  /**
   * Encerra o desenho no `mouseup` do DOM, e não no evento `dblclick`.
   *
   * São duas restrições combinadas. O app descobre que existe um polígono num
   * listener de `mouseup` na `window` (`usePoligonOnMap`), e o `dashboard.tsx`
   * usa esse estado para abrir o painel de filtros — então o polígono precisa
   * existir antes de o evento chegar à `window`, o que descarta o `dblclick`,
   * disparado depois do último `mouseup`. E o evento `mouseup` do próprio Maps
   * não serve: ele engole o do segundo clique de um par rápido, porque consome
   * essa sequência para o duplo-clique-para-zoom.
   *
   * Ouvindo o DOM no div do mapa, o handler roda na subida do evento antes da
   * `window` — a mesma posição em que o desenho à mão livre da lib se encaixa.
   * O `detail` é o contador de cliques do navegador, então o duplo clique é
   * reconhecido pelo limite do sistema operacional do usuário, sem heurística
   * de tempo.
   */
  private listenForRelease(map: google.maps.Map): void {
    const container = map.getDiv();
    const onRelease = (event: MouseEvent) => {
      if (event.detail >= 2) {
        this.finishPolygon();
      }
    };

    container.addEventListener('mouseup', onRelease);
    this.listeners.push({
      remove: () => container.removeEventListener('mouseup', onRelease),
    });
  }

  private finishPolygon(): void {
    const polygon = this.preview;
    const path = this.path;

    if (!polygon || !path) {
      return;
    }

    // O duplo clique vem precedido de cliques no mesmo ponto, que entraram como
    // vértices repetidos. Em vez de tentar adivinhar quantos foram, remove
    // qualquer repetição no fim do caminho.
    while (path.getLength() > 1) {
      const last = path.getAt(path.getLength() - 1);
      const previous = path.getAt(path.getLength() - 2);
      if (!last.equals(previous)) {
        break;
      }
      path.removeAt(path.getLength() - 1);
    }

    if (path.getLength() < MIN_POLYGON_VERTICES) {
      // Desenho degenerado: descarta e segue armado.
      this.discardPreview();
      this.armPolygon();
      return;
    }
    this.clearListeners();

    // Solta as referências antes de avisar, para o `setDrawingMode(null)` que a
    // lib dispara em seguida não apagar o polígono recém-concluído.
    this.preview = null;
    this.path = null;

    // O `dblclick` ainda vai chegar: o navegador o dispara depois do `mouseup`
    // que encerrou o desenho. Até lá o zoom por duplo clique segue suprimido,
    // e a marca precisa ser um estado porque o `trigger` abaixo faz a lib
    // chamar `setDrawingMode(null)`, que passa pelo `restoreMapInteraction`.
    this.pendingDoubleClick = true;
    this.restoreMapInteraction();

    window.setTimeout(() => {
      this.pendingDoubleClick = false;
      this.options.map?.setOptions({ disableDoubleClickZoom: false });
    }, DOUBLE_CLICK_ZOOM_RESTORE_MS);

    // O evento é disparado com o modo AINDA ativo de propósito: o
    // `stopDrawingManager` da lib remove o polígono se encontrar
    // `getDrawingMode() === null` neste instante. É ele que chama
    // `setDrawingMode(null)` em seguida.
    google.maps.event.trigger(this, 'polygoncomplete', polygon);
  }

  private discardPreview(): void {
    this.clearListeners();

    if (this.preview) {
      this.preview.setMap(null);
      this.preview = null;
    }
    this.path = null;
  }

  private clearListeners(): void {
    this.listeners.forEach((listener) =>
      google.maps.event.removeListener(listener)
    );
    this.listeners = [];
  }

  /** Mesmos valores do `enableMapMovement` da lib, para não divergir do resto. */
  private restoreMapInteraction(): void {
    this.options.map?.setOptions({
      draggableCursor: 'default',
      gestureHandling: 'cooperative',
      disableDoubleClickZoom: this.pendingDoubleClick,
    });
  }
}

/**
 * Confirma que o construtor real está quebrado antes de substituí-lo, para o
 * shim se desativar sozinho caso o Google volte atrás ou a lib passe a usar
 * outra API.
 */
const isDrawingManagerBroken = (): boolean => {
  const drawing = window.google?.maps?.drawing;

  if (!drawing?.DrawingManager) {
    return false;
  }

  try {
    // Sem argumentos: o construtor removido lança de qualquer forma, e um
    // construtor restaurado aceita options opcionais.
    new drawing.DrawingManager();
    return false;
  } catch {
    return true;
  }
};

let isPatched = false;

/**
 * Envolve o `Map.initMap` da lib para instalar o shim no único momento em que
 * isso é seguro: a lib só chama o `initMap` depois que `window.google.maps`
 * existe, e o construtor quebrado só é usado dentro dele.
 */
export const patchMapLibraryDrawingManager = (): void => {
  if (isPatched) {
    return;
  }
  isPatched = true;

  const originalInitMap = Map.initMap;

  Map.initMap = function patchedInitMap(
    ...args: Parameters<typeof originalInitMap>
  ): void {
    if (isDrawingManagerBroken()) {
      window.google.maps.drawing.DrawingManager =
        DrawingManagerShim as unknown as typeof google.maps.drawing.DrawingManager;
    }

    originalInitMap.apply(this, args);
  };
};
